import type {
  ConnectionInfo,
  ConversationInfo,
  SessionStartResponse,
} from "../types";

/**
 * Capture browser context for session properties.
 * Auto-captures landing page, referrer, UTM params, and cid from the current page.
 */
export function captureBrowserContext(): Record<string, string | null> {
  if (typeof window === "undefined") return {};

  const props: Record<string, string | null> = {
    landing_page: window.location.href,
    referrer: document.referrer || null,
  };

  // Capture UTM params and cid from URL
  const params = new URLSearchParams(window.location.search);
  const utmKeys = [
    "utm_campaign",
    "utm_source",
    "utm_medium",
    "utm_content",
    "utm_term",
  ];
  for (const key of utmKeys) {
    if (params.has(key)) {
      props[key] = params.get(key);
    }
  }
  if (params.has("cid")) {
    props.cid = params.get("cid");
  }

  return props;
}

/**
 * Default API URL for production
 */
const DEFAULT_API_URL = "https://api.tellcasey.com";

/**
 * Token refresh context passed to the refresh callback
 */
export interface TokenRefreshContext {
  /** The connection code (if known) */
  connectionCode?: string;
  /** The conversation ID (if known) */
  conversationId?: string;
}

/**
 * Token refresh callback type.
 * Called when the client needs a token and doesn't have one, or when a 401 is received.
 * Should return a valid token or throw an error.
 */
export type TokenRefreshCallback = (
  context: TokenRefreshContext,
) => Promise<string>;

export interface VoiceChatApiClientOptions {
  /** Base URL for API requests. Defaults to https://api.tellcasey.com */
  apiUrl?: string;
  /** Initial auth token (optional, can be set later via setToken) */
  token?: string;
  /**
   * Callback to refresh/fetch token when needed.
   * Called automatically when:
   * - Making an authenticated request without a token
   * - Receiving a 401 response
   */
  onTokenRefresh?: TokenRefreshCallback;
}

export interface CreateConversationOptions {
  /** The connection code */
  connectionCode: string;
  /** Optional session properties (UTM params, browser context, etc.) */
  sessionProperties?: Record<string, unknown>;
  /** Optional custom context to pass to the agent */
  customContext?: Record<string, unknown>;
  /** Transport provider: "daily" (default), "elevenlabs", or "livekit" */
  transport?: "daily" | "elevenlabs" | "livekit";
  /** Backend flavor for Daily/Pipecat: "legacy" (default) or "flow" */
  flavor?: "legacy" | "flow";
}

/**
 * API client for voice chat sessions.
 * Works with both embedded widgets (cross-origin) and web apps (same-origin).
 *
 * Uses the Hono API at /v1/client/* endpoints.
 */
export class VoiceChatApiClient {
  private apiUrl: string;
  private token: string | null = null;
  private onTokenRefresh: TokenRefreshCallback | null = null;
  private tokenRefreshPromise: Promise<string> | null = null;

  constructor(options: VoiceChatApiClientOptions = {}) {
    this.apiUrl = options.apiUrl || DEFAULT_API_URL;
    if (options.token) {
      this.token = options.token;
    }
    if (options.onTokenRefresh) {
      this.onTokenRefresh = options.onTokenRefresh;
    }
  }

  /**
   * Set the token refresh callback
   */
  setTokenRefreshCallback(callback: TokenRefreshCallback) {
    this.onTokenRefresh = callback;
  }

  /**
   * Set the API URL for requests (runtime override)
   */
  setApiUrl(url: string) {
    this.apiUrl = url;
  }

  /**
   * Set the auth token for authenticated requests
   */
  setToken(token: string) {
    this.token = token;
  }

  /**
   * Get the current auth token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if a token is set
   */
  hasToken(): boolean {
    return !!this.token;
  }

  /**
   * Clear the auth token
   */
  clearToken() {
    this.token = null;
  }

  /**
   * Ensure we have a valid token, refreshing if needed.
   * Uses the tokenRefreshCallback if set.
   * Deduplicates concurrent refresh calls.
   */
  private async ensureToken(context: TokenRefreshContext = {}): Promise<void> {
    // Already have a token
    if (this.token) {
      return;
    }

    // No refresh callback configured
    if (!this.onTokenRefresh) {
      return;
    }

    // Already refreshing - wait for that to complete
    if (this.tokenRefreshPromise) {
      await this.tokenRefreshPromise;
      return;
    }

    // Start refresh
    this.tokenRefreshPromise = this.onTokenRefresh(context);
    try {
      this.token = await this.tokenRefreshPromise;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Make an authenticated request, refreshing token if needed.
   * Handles 401 responses by refreshing token and retrying once.
   */
  private async authenticatedFetch(
    url: string,
    options: RequestInit,
    context: TokenRefreshContext = {},
  ): Promise<Response> {
    // Ensure we have a token before making the request
    await this.ensureToken(context);

    const response = await fetch(url, {
      ...options,
      headers: this.getAuthHeaders(),
    });

    // If 401 and we have a refresh callback, try refreshing and retry once
    if (response.status === 401 && this.onTokenRefresh) {
      this.token = null; // Clear invalid token
      await this.ensureToken(context);

      // Retry with new token
      return fetch(url, {
        ...options,
        headers: this.getAuthHeaders(),
      });
    }

    return response;
  }

  /**
   * Get auth headers for API requests
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Read connection info by code (validates the connection exists)
   * No authentication required - this is a public endpoint
   */
  async readConnectionByCode(connectionCode: string): Promise<ConnectionInfo> {
    const response = await fetch(
      `${this.apiUrl}/v1/client/connections/${encodeURIComponent(connectionCode)}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error?.message || "Failed to fetch connection");
    }

    const result = await response.json();

    if (result.error) {
      throw new Error(result.error.message || "Failed to fetch connection");
    }

    const data = result.data;
    if (!data) {
      throw new Error("Connection not found");
    }

    // Map snake_case API response to camelCase
    return {
      connectionCode: data.connection_code,
      authStyle: data.auth_style,
      organizationSlug: data.organization_slug,
      organizationName: data.organization_name,
      organizationAvatarUrl: data.organization_avatar_url,
      greetingTitle: data.greeting_title,
      greetingBody: data.greeting_body,
    };
  }

  /**
   * Fetch user's conversations by connection code (for session resumption)
   * Uses REST endpoint with Bearer token authentication
   * @param connectionCode - The connection code to filter by
   * @param options - Optional filters (e.g., flavor)
   */
  async getUserConversations(
    connectionCode: string,
    options?: { flavor?: "legacy" | "flow" | "elevenlabs" | "livekit" },
  ): Promise<ConversationInfo[]> {
    const params = new URLSearchParams();
    if (options?.flavor) {
      params.set("flavor", options.flavor);
    }
    const queryString = params.toString();
    const url = `${this.apiUrl}/v1/client/connections/${encodeURIComponent(connectionCode)}/conversations${queryString ? `?${queryString}` : ""}`;

    const response = await this.authenticatedFetch(
      url,
      { method: "GET" },
      { connectionCode },
    );

    if (!response.ok) {
      console.warn(
        "[VoiceChatApiClient] Failed to fetch conversations for resumption",
      );
      return [];
    }

    const result = await response.json();
    const data = result.data;

    if (!data || !data.conversations) {
      return [];
    }

    // Map snake_case API response to camelCase
    return data.conversations.map((conv: Record<string, unknown>) => ({
      id: conv.id,
      status: conv.status,
      connectionCode: conv.connection_code,
      createdAt: conv.created_at,
      conversation: conv.conversation,
      flavor: conv.flavor,
    }));
  }

  /**
   * Create a new conversation AND room in one call.
   * POST /v1/client/conversations
   */
  async createConversationWithRoom(
    options: CreateConversationOptions,
  ): Promise<SessionStartResponse> {
    const {
      connectionCode,
      sessionProperties,
      customContext,
      transport = "daily",
      flavor,
    } = options;

    const response = await this.authenticatedFetch(
      `${this.apiUrl}/v1/client/conversations`,
      {
        method: "POST",
        body: JSON.stringify({
          connection_code: connectionCode,
          session_properties: sessionProperties || {},
          custom_context: customContext,
          transport,
          ...(flavor && { flavor }),
        }),
      },
      { connectionCode },
    );

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error?.message || "Failed to create conversation");
    }

    const result = await response.json();
    const data = result.data;

    if (!data) {
      throw new Error("Failed to create conversation");
    }

    return {
      conversationId: data.conversation_id,
      transport: {
        type: data.transport.type,
        provider: data.transport.provider,
        sessionId: data.transport.session_id,
        // WebRTC fields
        url: data.transport.url,
        token: data.transport.token,
        // Telephony fields (for future use)
        callSid: data.transport.call_sid,
        toNumber: data.transport.to_number,
        fromNumber: data.transport.from_number,
        callStatus: data.transport.call_status,
      },
      participantUserId: data.participant_user_id,
      connectionCode: data.connection_code,
      reused: data.reused,
    };
  }

  /**
   * Get transport credentials for an existing conversation (resume/reconnect).
   * POST /v1/client/conversations/:id/rooms
   */
  async resumeSession(
    conversationId: string,
    options?: { flavor?: "legacy" | "flow" },
  ): Promise<SessionStartResponse> {
    const response = await this.authenticatedFetch(
      `${this.apiUrl}/v1/client/conversations/${conversationId}/rooms`,
      {
        method: "POST",
        body: JSON.stringify({
          ...(options?.flavor && { flavor: options.flavor }),
        }),
      },
      { conversationId },
    );

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error?.message || "Failed to resume session");
    }

    const result = await response.json();
    const data = result.data;

    if (!data) {
      throw new Error("Failed to resume session");
    }

    return {
      conversationId: data.conversation_id || conversationId,
      transport: {
        type: data.transport.type,
        provider: data.transport.provider,
        sessionId: data.transport.session_id,
        // WebRTC fields
        url: data.transport.url,
        token: data.transport.token,
        // Telephony fields (for future use)
        callSid: data.transport.call_sid,
        toNumber: data.transport.to_number,
        fromNumber: data.transport.from_number,
        callStatus: data.transport.call_status,
      },
      participantUserId: data.participant_user_id,
      connectionCode: data.connection_code,
      reused: data.reused,
    };
  }

  /**
   * Start a new session with browser context auto-captured
   */
  async startNewSession(
    connectionCode: string,
    sessionProperties?: Record<string, unknown>,
    customContext?: Record<string, unknown>,
  ): Promise<SessionStartResponse> {
    return this.createConversationWithRoom({
      connectionCode,
      sessionProperties: {
        ...captureBrowserContext(),
        ...(sessionProperties || {}),
      },
      customContext,
    });
  }

  /**
   * Get a conversation by ID.
   * GET /v1/client/conversations/:id
   */
  async getConversation(conversationId: string): Promise<ConversationInfo> {
    const response = await this.authenticatedFetch(
      `${this.apiUrl}/v1/client/conversations/${conversationId}`,
      { method: "GET" },
      { conversationId },
    );

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error?.message || "Failed to fetch conversation");
    }

    const result = await response.json();
    const data = result.data;

    if (!data) {
      throw new Error("Conversation not found");
    }

    // Map snake_case API response to camelCase
    return {
      id: data.id,
      status: data.status,
      connectionCode: data.connection_code,
      createdAt: data.created_at,
      conversation: data.conversation,
      flavor: data.flavor,
      context: data.context,
      sessionProperties: data.session_properties,
      participantUserId: data.participant_user_id,
      organizationId: data.organization_id,
    };
  }
}

/**
 * Create a new API client instance
 */
export function createVoiceChatApiClient(
  options?: VoiceChatApiClientOptions,
): VoiceChatApiClient {
  return new VoiceChatApiClient(options);
}
